// Regime Tilt — interactive bar chart + Growth/Momentum sliders.
//
// X = asset class, Y = % of portfolio. Two bars per asset (Baseline
// in slate, Tilted in emerald). Two sliders below the chart let the
// user explore *what-if* macro states by blending the four regime
// allocations in real time:
//   * Growth Score (G)        in [-2, +2]
//   * Momentum Score (MG)     in [-2, +2]
//
// The blend uses a softmax over (negative-) squared distance from the
// slider position to each regime's centroid in (G, MG) space. With
// sigma = 0.7 the weights are sharply peaked at the centroid so
// dragging the slider exactly to e.g. (1, 1) yields ~97% Expansion
// mass — the chart visually "snaps" to a single regime.
//
// The four canonical regime allocations are mirrored client-side from
// ``backend/app/api/macro.py::_REGIME_TILTS`` for zero-latency slider
// updates. KEEP IN SYNC manually until the API is extended to return
// all four regime allocations + rationales in one payload.
//
// Tooltip: Recharts uses each series' fill colour for ``itemStyle``
// text by default, which can render near-black on a dark
// ``contentStyle``. We force ``itemStyle`` + ``labelStyle`` to a light
// slate so every row stays legible.

import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    macroApi,
    type MacroRegimeState,
    type RegimeAllocation,
} from '../../../api/macro';

type RegimeLabel = 'Expansion' | 'Slowdown' | 'Contraction' | 'Recovery';

const REGIME_CENTROIDS: Record<RegimeLabel, { g: number; mg: number }> = {
    Expansion: { g: 1, mg: 1 },
    Slowdown: { g: 1, mg: -1 },
    Contraction: { g: -1, mg: -1 },
    Recovery: { g: -1, mg: 1 },
};

const REGIME_ALLOCATIONS: Record<RegimeLabel, Record<string, number>> = {
    Expansion: {
        equities: 50, etf: 15, commodities: 10, reits: 12,
        cryptocurrencies: 8, fx: 2, cash: 3,
    },
    Slowdown: {
        equities: 30, etf: 15, commodities: 20, reits: 8,
        cryptocurrencies: 2, fx: 10, cash: 15,
    },
    Contraction: {
        equities: 20, etf: 30, commodities: 5, reits: 5,
        cryptocurrencies: 2, fx: 13, cash: 25,
    },
    Recovery: {
        equities: 45, etf: 15, commodities: 5, reits: 15,
        cryptocurrencies: 7, fx: 5, cash: 8,
    },
};

const REGIME_RATIONALES: Record<RegimeLabel, string> = {
    Expansion:
        'Expansion heavily favors risk-on assets. Equities, REITs, and Crypto are overweighted, funded by drawing down Cash, defensive ETFs, and FX.',
    Slowdown:
        'Slowdown signals late-cycle stagflation risk. Equities and Crypto are underweighted to protect capital, while Commodities are heavily overweighted as an inflation hedge alongside Cash.',
    Contraction:
        'Contraction requires maximum defense. Heavy shift into Cash, FX (safe havens), and defensive ETFs, while aggressively underweighting Equities, REITs, and Commodities due to demand destruction.',
    Recovery:
        'Recovery is an early-cycle risk-on phase. Equities and REITs are overweighted to capture the rebound from bottom, while Commodities remain underweighted until inflation returns.',
};

const ASSET_ORDER: { key: string; label: string }[] = [
    { key: 'equities', label: 'Equities' },
    { key: 'etf', label: 'ETFs' },
    { key: 'commodities', label: 'Commodities' },
    { key: 'reits', label: 'REITs' },
    { key: 'cryptocurrencies', label: 'Crypto' },
    { key: 'fx', label: 'FX' },
    { key: 'cash', label: 'Cash' },
];

const REGIME_LABELS = Object.keys(REGIME_CENTROIDS) as RegimeLabel[];

const BLEND_SIGMA = 0.7;
const SLIDER_BOUND = 2.0;

interface RegimeWeights {
    weights: Record<RegimeLabel, number>;
    dominant: RegimeLabel;
}

function computeRegimeWeights(g: number, mg: number): RegimeWeights {
    const denom = 2 * BLEND_SIGMA * BLEND_SIGMA;
    const exps = REGIME_LABELS.map((label) => {
        const c = REGIME_CENTROIDS[label];
        const d2 = (g - c.g) ** 2 + (mg - c.mg) ** 2;
        return { label, e: Math.exp(-d2 / denom) };
    });
    const sum = exps.reduce((acc, x) => acc + x.e, 0);
    const weights = {} as Record<RegimeLabel, number>;
    let dominant: RegimeLabel = REGIME_LABELS[0];
    let bestW = -Infinity;
    for (const x of exps) {
        const w = x.e / sum;
        weights[x.label] = w;
        if (w > bestW) {
            bestW = w;
            dominant = x.label;
        }
    }
    return { weights, dominant };
}

function blendAllocation(
    weights: Record<RegimeLabel, number>,
): Record<string, number> {
    const result: Record<string, number> = {};
    for (const { key } of ASSET_ORDER) {
        let v = 0;
        for (const label of REGIME_LABELS) {
            v += weights[label] * REGIME_ALLOCATIONS[label][key];
        }
        result[key] = v;
    }
    return result;
}

function clampSlider(v: number | null | undefined): number {
    if (v == null || Number.isNaN(v)) return 0;
    return Math.max(-SLIDER_BOUND, Math.min(SLIDER_BOUND, v));
}

interface SliderRowProps {
    label: string;
    value: number;
    onChange: (v: number) => void;
    helpText?: string;
}

const SliderRow = ({ label, value, onChange, helpText }: SliderRowProps) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-bold">{label}</label>
            <span className="text-sm font-bold tabular-nums">
                {value.toFixed(1)}
            </span>
        </div>
        <input
            type="range"
            min={-SLIDER_BOUND}
            max={SLIDER_BOUND}
            step={0.1}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full accent-emerald-500 cursor-pointer"
            aria-label={label}
        />
        {helpText && <p className="text-xs muted">{helpText}</p>}
    </div>
);

export const RegimeTilt = () => {
    const [allocation, setAllocation] = useState<RegimeAllocation | null>(null);
    const [regimeState, setRegimeState] =
        useState<MacroRegimeState | null>(null);
    const [g, setG] = useState(0);
    const [mg, setMg] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [state, alloc] = await Promise.all([
                    macroApi.getCurrentRegime(),
                    macroApi.getRegimeAllocation(),
                ]);
                if (cancelled) return;
                setRegimeState(state);
                setAllocation(alloc);
                if (state) {
                    const label =
                        (state.regime_label as RegimeLabel) ?? 'Expansion';
                    const seedG =
                        state.indicators.g_score ??
                        REGIME_CENTROIDS[label]?.g ??
                        0;
                    const seedMg =
                        state.indicators.mg_score ??
                        REGIME_CENTROIDS[label]?.mg ??
                        0;
                    setG(clampSlider(seedG));
                    setMg(clampSlider(seedMg));
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error ? err.message : 'Failed to load.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const { weights, dominant } = useMemo(
        () => computeRegimeWeights(g, mg),
        [g, mg],
    );
    const dynamicAllocation = useMemo(
        () => blendAllocation(weights),
        [weights],
    );

    const chartData = useMemo(
        () =>
            ASSET_ORDER.map(({ key, label }) => ({
                asset: label,
                Baseline: allocation?.baseline_allocation[key] ?? 0,
                Tilted: dynamicAllocation[key] ?? 0,
            })),
        [allocation, dynamicAllocation],
    );

    if (loading) {
        return (
            <div className="surface-card-skeleton h-80" aria-busy>
                <p className="muted text-sm">Loading regime tilt…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="surface-card-error">
                <p className="font-bold">Regime Tilt unavailable</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    if (!allocation) {
        return (
            <div className="surface-card">
                <h3 className="section-title">Regime Tilt</h3>
                <p className="muted text-sm mt-2">
                    No regime tilt to apply yet — the macro pipeline has
                    not produced its first classification.
                </p>
            </div>
        );
    }

    return (
        <div className="surface-card">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="section-title">Regime Tilt</h3>
                    <p className="muted text-xs">
                        Baseline vs. macro-regime tilted allocation · auto-
                        detected:{' '}
                        <span className="font-bold">
                            {allocation.current_regime}
                        </span>
                        {regimeState?.date ? ` (${regimeState.date})` : ''}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-xs muted">Slider position closest to</p>
                    <p className="text-base font-black">{dominant}</p>
                </div>
            </div>

            <div className="mt-4 w-full h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                        barCategoryGap="22%"
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="currentColor"
                            opacity={0.15}
                        />
                        <XAxis
                            dataKey="asset"
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            interval={0}
                        />
                        <YAxis
                            domain={[0, 'dataMax + 5']}
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            tickFormatter={(v) => `${v}%`}
                            width={40}
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'rgba(15, 23, 42, 0.97)',
                                border: '1px solid #334155',
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            itemStyle={{ color: '#f8fafc' }}
                            labelStyle={{
                                color: '#cbd5e1',
                                fontWeight: 700,
                                marginBottom: 4,
                            }}
                            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                            formatter={(value: number, name: string) => [
                                `${value.toFixed(1)}%`,
                                name,
                            ]}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: 12 }}
                            iconType="square"
                        />
                        <Bar
                            dataKey="Baseline"
                            fill="#94a3b8"
                            radius={[4, 4, 0, 0]}
                            isAnimationActive
                            animationDuration={300}
                        />
                        <Bar
                            dataKey="Tilted"
                            fill="#10b981"
                            radius={[4, 4, 0, 0]}
                            isAnimationActive
                            animationDuration={300}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <SliderRow
                    label="Growth Score"
                    value={g}
                    onChange={setG}
                    helpText="Composite growth Z-score (-2 weak · +2 strong)"
                />
                <SliderRow
                    label="Momentum Score"
                    value={mg}
                    onChange={setMg}
                    helpText="Growth momentum (-2 decelerating · +2 accelerating)"
                />
            </div>

            <ul className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
                {REGIME_LABELS.map((label) => (
                    <li
                        key={label}
                        className="flex items-center justify-between gap-2 min-w-0"
                    >
                        <span className="truncate">{label}</span>
                        <span className="font-bold tabular-nums">
                            {Math.round(weights[label] * 100)}%
                        </span>
                    </li>
                ))}
            </ul>

            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {REGIME_RATIONALES[dominant]}
            </p>
        </div>
    );
};

export default RegimeTilt;
